import TimelineItem from "@/components/ui/timeline-item";
import KeyTakeaway from "@/components/ui/key-takeaway";

export default function PeptideHistory() {
  return (
    <section id="history" className="mb-16 scroll-mt-20">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
        Historical and Current Examples of Peptide Therapy
      </h2>
      
      <p className="text-lg mb-8">
        The story of peptide therapeutics spans a century of medical innovation, 
        from life-saving treatments to today's blockbuster medications.
      </p>
      
      {/* Historical Milestones */}
      <div className="flex flex-col space-y-8 mb-10">
        <div className="flex items-center">
          <div className="bg-blue-500 text-white flex items-center justify-center rounded-full w-16 h-16 flex-shrink-0 mr-4">
            <span className="text-xl font-bold">1921</span>
          </div>
          <div>
            <h3 className="text-xl font-bold">Insulin Discovery</h3>
            <p>First peptide therapy revolutionizing diabetes treatment</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <div className="bg-blue-500 text-white flex items-center justify-center rounded-full w-16 h-16 flex-shrink-0 mr-4">
            <span className="text-xl font-bold">1950s</span>
          </div>
          <div>
            <h3 className="text-xl font-bold">Expansion Era</h3>
            <p>Development of oxytocin, calcitonin, and other peptide therapies</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <div className="bg-blue-500 text-white flex items-center justify-center rounded-full w-16 h-16 flex-shrink-0 mr-4">
            <span className="text-xl font-bold">2000s</span>
          </div>
          <div>
            <h3 className="text-xl font-bold">GLP-1 Revolution</h3>
            <p>Mainstream adoption of peptide therapies for obesity and diabetes</p>
          </div>
        </div>
      </div>
      
      {/* Timeline */}
      <div className="relative mb-12 pl-12">
        <TimelineItem 
          year="1921-22"
          event="Insulin – The First Peptide Therapy"
          description="Insulin was the first peptide hormone ever discovered and one of the first peptide therapies. Frederick Banting and Charles Best isolated insulin at the University of Toronto in 1921, and it was first used to treat a human patient in 1922. Before insulin, diabetes was a death sentence. This discovery transformed it into a manageable condition, saving countless lives and establishing peptides as a therapeutic category. Impact: Revolutionized diabetes treatment; established peptides as viable therapeutics."
          last={false}
        />
        
        <TimelineItem 
          year="1950-90s"
          event="The Expansion Era"
          description="Throughout the latter half of the 20th century, scientists discovered and developed numerous peptide therapies for various conditions: Oxytocin (1950s) - Used in childbirth, Calcitonin (1960s) - For osteoporosis, Vasopressin analogs (1970s) - For diabetes insipidus, Leuprolide (1980s) - For prostate cancer and endometriosis, Octreotide (1980s) - For acromegaly and neuroendocrine tumors. Impact: Established peptides as a diverse therapeutic class across multiple medical specialties."
          last={false}
        />
        
        <TimelineItem 
          year="2000s+"
          event="The GLP-1 Revolution"
          description="The most talked-about new drugs for obesity and diabetes are GLP-1 analog peptides. Medications like semaglutide (Ozempic®, Wegovy®) and tirzepatide (Mounjaro®) are modified peptides that mimic GLP-1, a gut hormone that reduces appetite and regulates blood sugar. These medications have shown dramatic weight loss and health benefits, making peptides front-page news. Industry projections estimate the GLP-1 market could exceed $100 billion by 2030, with nearly 1 in 11 Americans potentially using a GLP-1 therapy. Impact: Mainstream adoption of peptide therapies; transformative effects on obesity and diabetes treatment."
          last={true}
        />
      </div>
      
      <div className="bg-[#EBF5FB] p-6 rounded-lg mb-8">
        <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">
          Peptides as a Class, Not a Single Drug
        </h3>
        <p className="mb-4">
          It's crucial to understand that "peptides" are a whole category of therapeutics. 
          Insulin and GLP-1 analogs are just two examples. In fact, since insulin's debut, 
          <strong> over 100 peptide drugs have been approved by the FDA</strong> for various conditions.
        </p>
        <p className="mb-4">
          Just as "small-molecule drug" could mean anything from aspirin to chemotherapy, 
          "peptide therapy" encompasses diverse treatments for conditions ranging from diabetes 
          to cancer. This breadth is why peptides are so exciting: scientists can design different 
          peptides for many different purposes.
        </p>
        
        <KeyTakeaway>
          "Peptides" doesn't refer to one specific substance or treatment – it's an entire therapeutic 
          modality, like saying "antibiotics" or "biologics." This diversity explains why peptides 
          have applications across so many medical specialties.
        </KeyTakeaway>
      </div>
      
      {/* Success Story */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">Case Study: GLP-1 Peptides and Metabolic Health</h3>
        </div>
        <div className="p-6">
          <p className="mb-4">
            GLP-1 (glucagon-like peptide-1) is a natural gut hormone that signals fullness and 
            helps regulate blood sugar. Scientists developed modified versions that last longer 
            in the body, creating powerful treatments for diabetes and obesity.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#ECF0F1] rounded-lg p-4">
              <h4 className="font-bold text-[#2C3E50] mb-2">The Problem</h4>
              <p className="text-sm">
                Natural GLP-1 breaks down in minutes, limiting therapeutic potential
              </p>
            </div>
            <div className="bg-[#ECF0F1] rounded-lg p-4">
              <h4 className="font-bold text-[#2C3E50] mb-2">The Innovation</h4>
              <p className="text-sm">
                Modified peptides that resist breakdown while preserving signaling
              </p>
            </div>
            <div className="bg-[#ECF0F1] rounded-lg p-4">
              <h4 className="font-bold text-[#2C3E50] mb-2">The Impact</h4>
              <p className="text-sm">
                15-20% weight loss and improved metabolic health parameters
              </p>
            </div>
          </div>
          
          <p className="text-sm text-[#7F8C8D] italic mb-4">
            GLP-1 therapies exemplify the potential of peptide-based treatments: they work with 
            the body's natural systems rather than against them, producing significant benefits 
            with manageable side effects.
          </p>
          
          <KeyTakeaway>
            The GLP-1 revolution demonstrates how peptide science is entering the mainstream. A recent report 
            estimates the GLP-1 drug market could top $100 billion by 2030, with nearly 1 in 11 Americans 
            potentially on a GLP-1 therapy—an unprecedented impact for a peptide class.
          </KeyTakeaway>
        </div>
      </div>
    </section>
  );
}
